/* Ardor Printing AI sign generator for the rigid sign designer (sections/ardor-rigid-idea.liquid, template
   page.ai-sign-generator). 2026-10-20. Ported from the AI real estate sign generator (assets/ardor-realestate-idea.js,
   itself from the AI banner stand, table throw, flag and banner generators): same look, Turnstile and email-unlock flow,
   the same visitor id + email (localStorage wsIdeaVisitor / wsIdeaEmail) and the same worker routes and counters
   (/api/banner/quota, /logo, /generate with product "banner", /fix, /final), so tries are shared with the other tools.
   The customer picks a material (keys as in the rigid sign designer and specs/rs-price-data.json: MAG, COR, ASB, AFW,
   ASW, ATR, PVC, FOM, STY, GAT, ALS), its size in inches (the designer's size chips or a custom size, same limits:
   shipped, pickup only, too big) and the side. The worker is asked for the whole sign face in feet (it clamps the drawing
   to 3:1 with plain extendable ends); each design is shown on a picture of the sign (on a wall, magnets on a vehicle
   door), ends filled from the design's own edges (composeEnds), plus the flat artwork. Sign idea chips (Grand Opening,
   Now Hiring...) fill an editable headline, style and style notes.
   "Use this design" -> /api/banner/final -> <builder>?ai=<finalId>&mat=<KEY>&w=<in>&h=<in>&face=<front|back>
   [&ends=smooth][&logo=<logoId>&lb=x0,y0,x1,y1], plus sessionStorage ardorRigidAiPick (format: RS-AI ai/HANDOFF.md).
   Before redirecting it fires a cancelable "ardor:rigid-ai-handoff" event on the section (detail.url, detail.pick);
   calling preventDefault() on it stops the redirect (used for testing).
   No jQuery and no libraries except Cloudflare Turnstile. */
(function () {
  'use strict';
  const root = document.querySelector('[data-ardor-rigid-ai]');
  if (!root || root.dataset.abiReady) return;
  root.dataset.abiReady = '1';

  const $ = (s, c = root) => c.querySelector(s);
  const $$ = (s, c = root) => Array.from(c.querySelectorAll(s));
  const API = (root.dataset.api || '').replace(/\/+$/, '');
  const SITEKEY = root.dataset.turnstile || '';
  const BUILDER = root.dataset.builder || '/products/custom-rigid-signs';
  const MIN_FT = 0.5; // the worker's banner mode takes 0.5 .. 200 ft per side
  const MAX_FT = 200;
  const MAX_LOGO_PX = 1536;
  const MAX_LOGO_BYTES = 8 * 1024 * 1024;
  const MAX_SOURCE_BYTES = 30 * 1024 * 1024;
  const LIMITS = { headline: 60, subline: 80, details: 120, notes: 300, fix: 200 };
  const STYLES = { bold: 'Bold', clean: 'Clean', fun: 'Fun', elegant: 'Elegant', vintage: 'Vintage', event: 'Event' };
  const LAST_KEY = 'ardorRigidAiLast';
  const PICK_KEY = 'ardorRigidAiPick';
  // [seconds, status text, step index (Layout, Your words, Spell check, Final touches), only when]
  const GEN_STEPS = [
    [0, 'Laying out your sign…', 0],
    [10, 'Setting your words…', 1],
    [22, 'Placing your logo…', 1, 'logo'],
    [34, 'Checking the spelling…', 2],
    [50, 'Double-checking every word…', 2],
    [66, 'Adding the final touches…', 3],
    [82, 'Almost there…', 3]
  ];

  /* ---------- storage (same keys as the sticker generator for visitor + email) ---------- */
  const store = {
    get(k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { window.localStorage.setItem(k, v); } catch (e) { /* private mode */ } }
  };
  const sget = (k) => { try { return JSON.parse(window.sessionStorage.getItem(k) || 'null'); } catch (e) { return null; } };
  const sset = (k, v) => { try { window.sessionStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* full or blocked */ } };

  let visitor = store.get('wsIdeaVisitor');
  if (!visitor || !/^[a-zA-Z0-9_-]{8,64}$/.test(visitor)) {
    visitor = (window.crypto && crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random().toString(36).slice(2)).replace(/[^a-zA-Z0-9_-]/g, '');
    store.set('wsIdeaVisitor', visitor);
  }
  let email = store.get('wsIdeaEmail') || '';

  function track(name, data) {
    try { if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish(name, data || {}); } catch (e) { /* ignore */ }
  }

  /* ---------- elements ---------- */
  const form = $('[data-abi-form]');
  const matChips = $$('[data-ars-mats] .abi__chip');
  const finRow = $('[data-ars-fins]');
  const finSub = $('[data-ars-sub-fin]');
  const sizeRow = $('[data-ars-sizes]');
  const customBox = $('[data-ars-custom]');
  const wIn = $('[data-ars-w]');
  const hIn = $('[data-ars-h]');
  const swapBtn = $('[data-ars-swap]');
  const limitsEl = $('[data-ars-limits]');
  const faceRow = $('[data-ars-faces]');
  const faceSub = $('[data-ars-sub-face]');
  const shapeCv = $('[data-ars-shape-cv]');
  const shapeLabel = $('[data-abi-shape-label]');
  const sizeNote = $('[data-abi-size-note]');
  const sizeErr = $('[data-abi-size-err]');
  const drop = $('[data-abi-drop]');
  const fileIn = $('[data-abi-file]');
  const logoRow = $('[data-abi-logo]');
  const logoImg = $('[data-abi-logo-img]');
  const logoName = $('[data-abi-logo-name]');
  const logoInfo = $('[data-abi-logo-info]');
  const logoErr = $('[data-abi-logo-err]');
  const lineIns = { headline: $('[data-abi-line="headline"]'), subline: $('[data-abi-line="subline"]'), details: $('[data-abi-line="details"]') };
  const headErr = $('[data-abi-headline-err]');
  const notesIn = $('[data-abi-notes]');
  const styleBtns = $$('[data-style]');
  const goBtn = $('[data-abi-go]');
  const goLabel = $('[data-abi-go-label]');
  const quotaEl = $('[data-abi-quota]');
  const errEl = $('[data-abi-err]');
  const results = $('[data-abi-results]');
  const metaEl = $('[data-abi-meta]');
  const echo = $('[data-abi-echo]');
  const h2 = $('[data-abi-h2]');
  const statusEl = $('[data-abi-status]');
  const loading = $('[data-abi-loading]');
  const bar = $('[data-abi-bar]');
  const stepEls = $$('[data-abi-steps] li');
  const grid = $('[data-abi-grid]');
  const againRow = $('[data-abi-again-row]');
  const againBtn = $('[data-abi-again]');
  const editBtn = $('[data-abi-edit]');
  const fineEl = $('.abi__fine');
  const gate = $('[data-abi-gate]');
  const gateForm = $('[data-abi-gate-form]');
  const gateEmail = $('[data-abi-email]');
  const gateErr = $('[data-abi-gate-err]');
  const gateN = $('[data-abi-gate-n]');

  const S = {
    mat: 'COR', iw: 24, ih: 18, sizeKey: '24x18', bad: '', face: 'front', idea: '', w: 0, h: 0, style: 'bold',
    logo: null, // { blob, w, h, name, id, thumb (object URL or data URL), thumbData }
    batch: null, // { batchId, aspect, clamped, w, h, style, notes, lines, logoId, images }
    busy: false, off: false,
    bannersLeft: null, fixesLeft: null, finalsLeft: null,
    hasEmail: !!email, freeBanners: 2, emailBanners: 10,
    pending: null
  };

  if (!API) {
    goBtn.disabled = true;
    quotaEl.hidden = true;
    return;
  }

  /* ---------- small helpers ---------- */
  const clean = (s) => String(s || '').replace(/\s+/g, ' ').trim();
  const round2 = (n) => Math.round(n * 100) / 100;
  const fmt = (n) => String(round2(n));
  const sizeLabel = (w, h) => `${fmt(w)} x ${fmt(h)} ft`;
  const num = (v) => { const n = parseFloat(String(v).replace(',', '.')); return Number.isFinite(n) ? n : NaN; };
  const validFt = (n) => Number.isFinite(n) && n >= MIN_FT && n <= MAX_FT;
  const clampAr = (a) => Math.min(3, Math.max(1 / 3, a));
  const shapeOf = (a) => (a >= 6 ? 'xwide' : a >= 1.3 ? 'wide' : a > 0.77 ? 'square' : 'tall');
  const enc = encodeURIComponent;

  /* ---------- rigid signs: materials, sizes and the sign picture ---------- */
  // Material keys match the rigid sign designer (/products/custom-rigid-signs) and specs/rs-price-data.json (table built by
  // RS-AI tools/gen_mats.py from that file: names, minimum, shipped and pickup-only maximums, sides and default corners /
  // standoffs are read from it, never typed). Per material: k = key, c = card (ACR = the 3 acrylic finishes), n = full name,
  // cn = card label, fn = finish label (acrylic), s = noun, sd = sides it can have (1 or 2), mn = minimum [w, h] in,
  // sm = shipped maximum [long side, short side] in, pm = pickup-only maximums [[long, short], ...] (printed in-house,
  // Snohomish shop), sz = size chips [w, h(, 1 = pickup only)] in the designer's order ('c' = Custom size), d = default
  // [w, h], r = default corner radius (in), hw = default holes / standoffs (s4 | s2 | sc | s6 standoffs, h4 ... holes),
  // t = board thickness (in), e = edge color, sc = scene of the picture (wall | door), nt = what it is (facts only).
  const MATS = [
    { k: "MAG", c: "MAG", n: "Magnets", cn: "Magnets", fn: "", s: "magnet", sd: 1, mn: [6, 6], sm: [96, 36], pm: [], sz: [[12, 18], [18, 24], [24, 18], [24, 36], [36, 24], [36, 48], [96, 36], "c"], d: [24, 18], r: 0.5, hw: "", t: 0.03, e: "#33363b", sc: "door", nt: "30 mil flexible magnet with a matte finish, made for car doors and other flat steel surfaces." },
    { k: "COR", c: "COR", n: "Coroplast", cn: "Coroplast", fn: "", s: "coroplast sign", sd: 2, mn: [6, 6], sm: [48, 38], pm: [[96, 48], [120, 48], [120, 60]], sz: [[18, 12], [18, 24], [24, 6], [24, 16], [24, 18], [24, 24], [24, 36], [36, 24], "c", [48, 96, 1], [48, 120, 1], [60, 120, 1]], d: [24, 18], r: 0, hw: "", t: 0.157, e: "#dfe2e7", sc: "wall", nt: "4 mm white corrugated plastic (coroplast) for outdoor signs. It works with wire H-stakes." },
    { k: "ASB", c: "ASB", n: "Aluminum Sandwich Board", cn: "Aluminum Sandwich Board", fn: "", s: "aluminum sandwich board sign", sd: 2, mn: [6, 6], sm: [48, 38], pm: [[96, 48]], sz: [[12, 18], [18, 24], [24, 18], [24, 36], [36, 24], [36, 48], [48, 36], "c", [48, 96, 1]], d: [24, 18], r: 0.25, hw: "", t: 0.125, e: "#aeb4bd", sc: "wall", nt: "1/8 in aluminum sandwich board: two aluminum sheets bonded to a plastic core, for long-term outdoor and indoor signs." },
    { k: "AFW", c: "ACR", n: "Clear Acrylic - Full White Backing", cn: "Acrylic", fn: "Full White Backing", s: "acrylic sign", sd: 1, mn: [6, 6], sm: [48, 38], pm: [[96, 48]], sz: [[12, 18], [18, 24], [24, 18], [24, 36], [36, 24], [36, 48], [48, 36], "c", [48, 96, 1]], d: [24, 18], r: 0, hw: "s4", t: 0.25, e: "rgba(176,226,236,.9)", sc: "wall", nt: "1/4 in clear acrylic printed on the back, then backed with white so the colors look their brightest." },
    { k: "ASW", c: "ACR", n: "Clear Acrylic - Spot White Backing", cn: "Acrylic", fn: "Spot White Backing", s: "acrylic sign", sd: 1, mn: [6, 6], sm: [48, 38], pm: [[96, 48]], sz: [[12, 18], [18, 24], [24, 18], [24, 36], [36, 24], [36, 48], [48, 36], "c", [48, 96, 1]], d: [24, 18], r: 0, hw: "s4", t: 0.25, e: "rgba(176,226,236,.9)", sc: "wall", nt: "1/4 in clear acrylic printed on the back, with white ink behind the printed areas." },
    { k: "ATR", c: "ACR", n: "Clear Acrylic - Transparent (no white)", cn: "Acrylic", fn: "Transparent (no white)", s: "acrylic sign", sd: 1, mn: [6, 6], sm: [48, 38], pm: [[96, 48]], sz: [[12, 18], [18, 24], [24, 18], [24, 36], [36, 24], [36, 48], [48, 36], "c", [48, 96, 1]], d: [24, 18], r: 0, hw: "s4", t: 0.25, e: "rgba(176,226,236,.9)", sc: "wall", nt: "1/4 in clear acrylic printed on the back with no white: white areas of your design look clear and the colors are see-through." },
    { k: "PVC", c: "PVC", n: "PVC Board", cn: "PVC Board", fn: "", s: "PVC sign", sd: 2, mn: [6, 6], sm: [48, 38], pm: [[96, 48]], sz: [[12, 18], [18, 24], [24, 18], [24, 36], [36, 24], [36, 48], [48, 36], "c", [48, 96, 1]], d: [24, 18], r: 0, hw: "", t: 0.125, e: "#e9ecef", sc: "wall", nt: "1/8 in white PVC board. It is water-resistant, for short-term outdoor and long-term indoor signs." },
    { k: "FOM", c: "FOM", n: "Foam Board", cn: "Foam Board", fn: "", s: "foam board sign", sd: 2, mn: [6, 6], sm: [48, 38], pm: [[96, 48]], sz: [[12, 18], [18, 24], [24, 18], [24, 36], [36, 24], [36, 48], [48, 36], "c", [48, 96, 1]], d: [24, 18], r: 0, hw: "", t: 0.1875, e: "#f1f1ee", sc: "wall", nt: "3/16 in white foam board for indoor use, like easels and wall signs." },
    { k: "STY", c: "STY", n: "Styrene", cn: "Styrene", fn: "", s: "styrene sign", sd: 1, mn: [6, 6], sm: [96, 48], pm: [[96, 48]], sz: [[12, 18], [18, 24], [24, 18], [24, 36], [36, 24], [36, 48], [48, 36], [48, 96], "c"], d: [24, 18], r: 0, hw: "", t: 0.02, e: "#e6e8eb", sc: "wall", nt: ".020 in white styrene, a thin flexible plastic for indoor and outdoor signs." },
    { k: "GAT", c: "GAT", n: "GatorFoam", cn: "GatorFoam", fn: "", s: "GatorFoam sign", sd: 2, mn: [6, 6], sm: [48, 38], pm: [[96, 48]], sz: [[12, 18], [18, 24], [24, 18], [24, 36], [36, 24], [36, 48], [48, 36], "c", [48, 96, 1]], d: [24, 18], r: 0, hw: "", t: 0.1875, e: "#ece9e2", sc: "wall", nt: "GatorFoam is a foam board with hard faces, stronger than regular foam board. You pick 3/16 in or 1/2 in, white or black, in the designer." },
    { k: "ALS", c: "ALS", n: "Aluminum Sign", cn: "Aluminum Sign", fn: "", s: "aluminum sign", sd: 2, mn: [6, 6], sm: [48, 38], pm: [], sz: ["c", [12, 18], [18, 24], [24, 36], [18, 12], [24, 18], [36, 24]], d: [12, 18], r: 0.75, hw: "", t: 0.04, e: "#c3c9d1", sc: "wall", nt: ".040 in solid aluminum sign for outdoor use. It does not rust." }
  ];
  const DEFAULT_MAT = 'COR';
  const FACE_NAMES = { front: 'Front', back: 'Back' };
  const matOf = (k) => MATS.find((m) => m.k === String(k || '').toUpperCase()) || null;
  const fmtIn = (n) => String(Math.round(n * 100) / 100);
  const sizeTxt = (w, h) => `${fmtIn(w)} x ${fmtIn(h)} in`;
  const keyOf = (w, h) => `${fmtIn(w)}x${fmtIn(h)}`;
  // The designer's size test (same as its price function): shipped when the long side <= sm[0] and the short side <= sm[1]
  // (either way); else pickup only when it fits one of pm; else too big. Minimum mn.
  function fitOf(m, w, h) {
    if (!m || !Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return 'bad';
    if (w < m.mn[0] - 1e-9 || h < m.mn[1] - 1e-9) return 'small';
    const L = Math.max(w, h), Sh = Math.min(w, h);
    if (L <= m.sm[0] + 1e-9 && Sh <= m.sm[1] + 1e-9) return 'ship';
    if (m.pm.some((p) => L <= p[0] + 1e-9 && Sh <= p[1] + 1e-9)) return 'pickup';
    return 'big';
  }
  const sizeOk = (m, w, h) => { const f = fitOf(m, w, h); return f === 'ship' || f === 'pickup'; };
  const faceOk = (m, id) => id === 'front' || (id === 'back' && !!m && m.sd > 1);
  const faceName = (id) => FACE_NAMES[id] || FACE_NAMES.front;
  // the biggest size of material m (pickup-only maximum when bigger than the shipped one)
  const maxOf = (m) => m.pm.reduce((a, p) => (p[0] * p[1] > a[0] * a[1] ? p : a), m.sm);
  // "24 x 18 in coroplast sign · Front" (the side only on materials that can be 2 sided)
  const signLabel = (k, w, h, id) => { const m = matOf(k) || matOf(DEFAULT_MAT); return `${sizeTxt(w, h)} ${m.s}${m.sd > 1 ? ` · ${faceName(id)}` : ''}`; };
  const signShort = (b) => { const m = matOf(b.mat) || matOf(DEFAULT_MAT); return `${sizeTxt(b.iw, b.ih)} ${m.s}`; };
  // What the worker is asked for (feet): the whole sign face (rigid signs have no bleed and no hidden edges). The worker's
  // banner mode takes 0.5 .. 200 ft a side and draws at most 3:1 (or 1:3), keeping the outer ends plain; signs beyond that
  // get their ends filled from the design's own edges. The 6 in minimum is the worker's 0.5 ft, so the shape is kept.
  function reqOf(w, h) {
    let a = w / 12, b = h / 12;
    const lo = Math.min(a, b);
    if (lo < MIN_FT) { const k = MIN_FT / lo; a *= k; b *= k; }
    return { w: Math.max(MIN_FT, round2(a)), h: Math.max(MIN_FT, round2(b)) };
  }

  // ---- drawing: the sign on a wall (magnets: on a vehicle door), front view, the design on its face ----
  function panelArt(img, pw, ph, smooth, px, mark) {
    if (img) return composeEnds(img, pw / ph, smooth, Math.max(48, Math.min(1400, Math.round(px))));
    const cv = mkC(Math.max(60, Math.round(px * (pw > ph ? pw / ph : 1))), Math.max(60, Math.round(px * (ph > pw ? ph / pw : 1))));
    const x = cv.getContext('2d');
    x.fillStyle = '#f7d9db';
    x.fillRect(0, 0, cv.width, cv.height);
    if (mark) {
      x.fillStyle = '#b3141f';
      x.textAlign = 'center';
      x.textBaseline = 'middle';
      if (cv.width / cv.height > 2.6) {
        const fs = Math.min(cv.width / 9, cv.height / 2);
        x.font = `800 ${Math.round(fs)}px Unbounded, Arial, sans-serif`;
        x.fillText('YOUR DESIGN', cv.width / 2, cv.height / 2);
      } else {
        const fs = Math.min(cv.width / 6.4, cv.height / 3.2);
        x.font = `800 ${Math.round(fs)}px Unbounded, Arial, sans-serif`;
        x.fillText('YOUR', cv.width / 2, cv.height / 2 - fs * 0.6);
        x.fillText('DESIGN', cv.width / 2, cv.height / 2 + fs * 0.6);
      }
    }
    return cv;
  }
  // rounded rectangle path
  function rr(x, x0, y0, w, h, r) {
    x.beginPath();
    r = Math.max(0, Math.min(r, w / 2, h / 2));
    x.moveTo(x0 + r, y0);
    x.arcTo(x0 + w, y0, x0 + w, y0 + h, r);
    x.arcTo(x0 + w, y0 + h, x0, y0 + h, r);
    x.arcTo(x0, y0 + h, x0, y0, r);
    x.arcTo(x0, y0, x0 + w, y0, r);
    x.closePath();
  }
  // default holes / standoffs [cx, cy, dia, kind] in sign inches (acrylic: centres 1.5 in from the edges, 1 in standoffs)
  function hwPts(m, w, h) {
    const c = m.hw || '', i = 1.5;
    const c4 = [[i, i], [w - i, i], [i, h - i], [w - i, h - i]], ct = [[w / 2, i], [w / 2, h - i]];
    const p = c[1] === '4' ? c4 : c[1] === '2' ? c4.slice(0, 2) : c[1] === 'c' ? ct : c[1] === '6' ? c4.concat(ct) : [];
    return p.map((q) => [q[0], q[1], c[0] === 's' ? 1 : 0.375, c[0]]);
  }
  // scene around a w x h in sign (sign at [0, 0, w, h]); inches. px / py = margin left-right / top-bottom (each from its
  // own side, so long thin signs still fill the picture), d = how much of the board edge shows (thickness, drawn a little
  // larger so thin boards still read as boards), r = corner radius.
  function signGeo(m, w, h) {
    const M = Math.max(w, h), door = m.sc === 'door';
    const px = (w * 0.12 + M * 0.04 + 2) * (door ? 1.6 : 1), py = (h * 0.12 + M * 0.04 + 2) * (door ? 1.25 : 1);
    const g = { w, h, M, px, py, door, r: Math.min(m.r || 0, w / 2, h / 2), d: Math.min(M * 0.02, Math.max(m.t * 2.5, M * 0.006)) };
    g.x0 = -px;
    g.x1 = w + px;
    g.y0 = -py;
    g.y1 = h + py;
    return g;
  }
  function drawScene(x, m, g, k) {
    const W = g.x1 - g.x0, H = g.y1 - g.y0, rad = Math.min(W, H) * 0.05;
    rr(x, g.x0, g.y0, W, H, rad);
    if (g.door) {
      // a vehicle door: light body color, the door gaps left and right, a body line and the handle
      const lg = x.createLinearGradient(0, g.y0, 0, g.y1);
      lg.addColorStop(0, '#f5f7f9'); lg.addColorStop(0.55, '#dde2e8'); lg.addColorStop(1, '#bfc6cf');
      x.fillStyle = lg;
      x.fill();
      x.save();
      rr(x, g.x0, g.y0, W, H, rad); x.clip();
      const gx0 = g.x0 + g.px * 0.3, gx1 = g.x1 - g.px * 0.3, ly = g.y0 + g.py * 0.55;
      x.strokeStyle = 'rgba(38,44,52,.5)'; x.lineWidth = 1.6 / k;
      x.beginPath(); x.moveTo(gx0, g.y0); x.lineTo(gx0, g.y1); x.moveTo(gx1, g.y0); x.lineTo(gx1, g.y1); x.stroke();
      x.strokeStyle = 'rgba(255,255,255,.85)'; x.lineWidth = 2.2 / k;
      x.beginPath(); x.moveTo(g.x0, ly); x.lineTo(g.x1, ly); x.stroke();
      x.strokeStyle = 'rgba(0,0,0,.12)'; x.lineWidth = 1 / k;
      x.beginPath(); x.moveTo(g.x0, ly + 2 / k); x.lineTo(g.x1, ly + 2 / k); x.stroke();
      const hw = Math.min(8, g.px * 0.6), hh = Math.max(0.6, Math.min(hw * 0.26, g.py * 0.3));
      x.fillStyle = '#8f98a3';
      rr(x, gx1 - hw - g.px * 0.15, g.y0 + g.py * 0.12, hw, hh, hh / 2); x.fill();
      x.restore();
    } else {
      // a light wall
      const lg = x.createLinearGradient(g.x0, g.y0, g.x1, g.y1);
      lg.addColorStop(0, '#ece8e2'); lg.addColorStop(1, '#d4cec5');
      x.fillStyle = lg;
      x.fill();
    }
  }
  function drawBoard(x, m, g, art, k) {
    const { w, h, r, d } = g, acr = m.c === 'ACR';
    // soft shadow on the wall, then the board edge (its thickness, seen down and to the right)
    x.save();
    x.shadowColor = 'rgba(0,0,0,.34)';
    x.shadowBlur = Math.max(3, g.M * 0.028 * k);
    x.shadowOffsetX = g.M * 0.006 * k;
    x.shadowOffsetY = g.M * 0.012 * k;
    rr(x, d * 0.5, d, w, h, r); x.fillStyle = acr ? 'rgba(150,200,210,.55)' : m.e; x.fill();
    x.restore();
    rr(x, d * 0.5, d, w, h, r); x.fillStyle = m.e; x.fill();
    // the face: the art fills the whole sign (no bleed); no art = a plain sign with grey lines
    x.save();
    rr(x, 0, 0, w, h, r); x.clip();
    if (art) x.drawImage(art, 0, 0, w, h);
    else {
      x.fillStyle = '#eceef2'; x.fillRect(0, 0, w, h);
      x.fillStyle = 'rgba(0,0,0,.08)';
      const n = Math.max(2, Math.min(6, Math.round(h / 5)));
      for (let i = 1; i < n; i++) x.fillRect(w * 0.14, (h * i) / n - h * 0.02, w * 0.72, h * 0.04);
    }
    if (acr) {
      // clear acrylic face: a soft gloss over the print
      const lg = x.createLinearGradient(0, 0, w, h);
      lg.addColorStop(0, 'rgba(255,255,255,.26)'); lg.addColorStop(0.42, 'rgba(255,255,255,.04)'); lg.addColorStop(0.6, 'rgba(255,255,255,0)'); lg.addColorStop(1, 'rgba(255,255,255,.12)');
      x.fillStyle = lg; x.fillRect(0, 0, w, h);
    }
    x.restore();
    x.lineWidth = (acr ? 1.6 : 1.1) / k;
    x.strokeStyle = acr ? 'rgba(255,255,255,.7)' : 'rgba(0,0,0,.3)';
    rr(x, 0, 0, w, h, r); x.stroke();
    // standoffs (chrome caps) or holes
    hwPts(m, w, h).forEach((q) => {
      const [cx, cy, dia, kind] = q, rad = dia / 2;
      x.beginPath(); x.arc(cx, cy, rad, 0, Math.PI * 2);
      if (kind === 's') {
        const rg = x.createRadialGradient(cx - rad * 0.35, cy - rad * 0.35, rad * 0.1, cx, cy, rad);
        rg.addColorStop(0, '#ffffff'); rg.addColorStop(0.45, '#c7ccd3'); rg.addColorStop(1, '#6d747d');
        x.fillStyle = rg; x.fill();
        x.lineWidth = 0.8 / k; x.strokeStyle = 'rgba(0,0,0,.4)'; x.stroke();
      } else {
        x.fillStyle = '#5b6068'; x.fill();
      }
    });
  }
  // size labels under and beside the sign (step 1 picture), drawn in pixels so small signs keep readable text
  function drawDims(x, g, k) {
    const P = (vx, vy) => [(vx - g.x0) * k, (vy - g.y0) * k];
    const fs = Math.max(11, Math.min(16, Math.min(g.px, g.py) * k * 0.34));
    x.save();
    x.setTransform(1, 0, 0, 1, 0, 0);
    x.font = `700 ${fs}px Jost, Arial, sans-serif`;
    x.textAlign = 'center';
    x.textBaseline = 'middle';
    x.fillStyle = 'rgba(24,26,30,.82)';
    x.strokeStyle = 'rgba(24,26,30,.5)';
    x.lineWidth = 1.2;
    const dim = (a, b, txt, vertical) => {
      const len = vertical ? b[1] - a[1] : b[0] - a[0];
      const tw = x.measureText(txt).width + 10;
      const cx = (a[0] + b[0]) / 2, cy = (a[1] + b[1]) / 2;
      x.save();
      x.translate(cx, cy);
      if (vertical) x.rotate(-Math.PI / 2);
      const half = len / 2, t = 4;
      x.beginPath();
      if (half > tw / 2 + 4) { x.moveTo(-half, 0); x.lineTo(-tw / 2, 0); x.moveTo(tw / 2, 0); x.lineTo(half, 0); }
      x.moveTo(-half, -t); x.lineTo(-half, t); x.moveTo(half, -t); x.lineTo(half, t);
      x.stroke();
      x.fillText(txt, 0, 0);
      x.restore();
    };
    const yb = g.h + g.d + (g.py - g.d) * 0.5, xr = g.w + g.d * 0.5 + (g.px - g.d * 0.5) * 0.5;
    dim(P(0, yb), P(g.w, yb), `${fmtIn(g.w)} in`, false);
    dim(P(xr, 0), P(xr, g.h), `${fmtIn(g.h)} in`, true);
    x.restore();
  }
  // Draws material key at w x h in with the design (img) on its face, filled the way the designer fills it (composeEnds).
  // img = null + mark = the step 1 picture with "YOUR DESIGN" and the size labels. Fits wPx x hPx.
  function drawSign(cv, img, key, w, h, smooth, wPx, hPx, mark) {
    const m = matOf(key) || matOf(DEFAULT_MAT);
    const g = signGeo(m, w, h);
    const k = Math.min(wPx / (g.x1 - g.x0), hPx / (g.y1 - g.y0));
    cv.width = Math.max(2, Math.round((g.x1 - g.x0) * k));
    cv.height = Math.max(2, Math.round((g.y1 - g.y0) * k));
    const x = cv.getContext('2d');
    x.setTransform(k, 0, 0, k, -g.x0 * k, -g.y0 * k);
    x.imageSmoothingEnabled = true;
    x.imageSmoothingQuality = 'high';
    drawScene(x, m, g, k);
    const art = img || mark ? panelArt(img, w, h, smooth, Math.min(w, h) * k * 1.4, mark) : null;
    drawBoard(x, m, g, art, k);
    if (mark) drawDims(x, g, k);
    return cv;
  }
  function signPic(img, b, smooth, hPx) {
    const cv = document.createElement('canvas');
    cv.className = 'ars__cv';
    return drawSign(cv, img, b.mat, b.iw, b.ih, smooth, hPx * 1.6, hPx, false);
  }
  // results cards: the sign picture beside the flat art, or above it for wide signs; the card frame takes about the
  // picture's shape (row: picture ~58% of the width, col: ~68% of the height) so it is not mostly empty
  const layoutOf = (b) => (b.iw / b.ih > 1.6 ? 'col' : 'row');
  const frameArOf = (b) => {
    const m = b && matOf(b.mat);
    if (!m || !(b.iw > 0) || !(b.ih > 0)) return 1;
    const g = signGeo(m, b.iw, b.ih), a = (g.x1 - g.x0) / (g.y1 - g.y0);
    return round2(layoutOf(b) === 'col' ? Math.max(1, Math.min(2.4, a * 0.74)) : Math.max(0.9, Math.min(2, a * 1.59)));
  };
  function cardPic(cv, img, b) {
    return drawSign(cv, img, b.mat, b.iw, b.ih, false, layoutOf(b) === 'col' ? 1000 : 640, layoutOf(b) === 'col' ? 560 : 1000, false);
  }
  function showErr(msg, el = errEl) { el.textContent = msg || ''; el.hidden = !msg; }
  function setInvalid(el, bad) { if (bad) el.setAttribute('aria-invalid', 'true'); else el.removeAttribute('aria-invalid'); }
  function withEmail(body) { if (S.hasEmail && email) body.email = email; return body; }

  function setQuota() {
    if (S.bannersLeft == null) return;
    const n = S.bannersLeft;
    const s = n === 1 ? '' : 's';
    if (S.hasEmail) quotaEl.textContent = n > 0 ? `${n} tr${n === 1 ? 'y' : 'ies'} left today` : 'No tries left today';
    else quotaEl.textContent = n > 0 ? `${n} free tr${n === 1 ? 'y' : 'ies'} · 4 designs each · no sign-up` : `Add your email for ${S.emailBanners} tries a day`;
    if (gateN) gateN.textContent = String(S.emailBanners);
    $$('.abi__fix-left', grid).forEach((el) => { el.textContent = fixesText(); });
  }
  function fixesText() {
    if (S.fixesLeft == null) return '';
    return `${S.fixesLeft} fix${S.fixesLeft === 1 ? '' : 'es'} left today`;
  }

  /* ---------- API ---------- */
  async function api(path, body, timeoutMs = 180000) {
    const opts = {};
    if (body instanceof FormData) { opts.method = 'POST'; opts.body = body; }
    else if (body) { opts.method = 'POST'; opts.headers = { 'Content-Type': 'application/json' }; opts.body = JSON.stringify(body); }
    let timer = 0;
    if (window.AbortController) {
      const ctl = new AbortController();
      opts.signal = ctl.signal;
      timer = setTimeout(() => ctl.abort(), timeoutMs);
    }
    let r;
    try {
      r = await fetch(API + path, opts);
    } catch (e) {
      clearTimeout(timer);
      if (e && e.name === 'AbortError') return { ok: false, error: 'timeout', message: 'This is taking longer than usual. Please try again.' };
      return { ok: false, error: 'network', message: 'Check your connection and try again.' };
    }
    let j = {};
    try { j = await r.json(); } catch (e) { j = {}; }
    clearTimeout(timer);
    if (!j || typeof j !== 'object') j = {};
    if (r.status === 402 && !j.error) j.error = 'need_email';
    if (!r.ok) {
      if (!j.error) j = { ok: false, error: 'server', message: 'Something went wrong on our side. Please try again.' };
      j.ok = false;
    }
    return j;
  }

  /* ---------- Turnstile (same approach as the sticker generator: explicit render, invisible, fresh token per POST) ---------- */
  let tsReady = null;
  let tsWidget = null;
  let tsResolve = null;
  function loadTurnstile() {
    if (!SITEKEY) return Promise.resolve(false);
    if (tsReady) return tsReady;
    tsReady = new Promise((res) => {
      if (window.turnstile && window.turnstile.render) { res(true); return; }
      window.abiTurnstileLoaded = () => res(true);
      const s = document.createElement('script');
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=abiTurnstileLoaded';
      s.async = true;
      s.defer = true;
      s.onerror = () => res(false);
      document.head.appendChild(s);
    });
    return tsReady;
  }
  async function botToken() {
    if (!SITEKEY || !(await loadTurnstile()) || !window.turnstile) return '';
    return new Promise((res) => {
      tsResolve = res;
      const take = (t) => { const r = tsResolve; if (r) { tsResolve = null; r(t || ''); } };
      try {
        if (tsWidget === null) {
          tsWidget = window.turnstile.render($('[data-abi-turnstile]'), {
            sitekey: SITEKEY,
            execution: 'execute',
            appearance: 'interaction-only',
            callback: (t) => take(t),
            'error-callback': () => take('')
          });
        } else {
          window.turnstile.reset(tsWidget);
        }
        window.turnstile.execute(tsWidget);
      } catch (e) { take(''); }
      setTimeout(() => { if (tsResolve === res) take(''); }, 45000);
    });
  }

  /* ---------- sign: material, finish (acrylic), size (chips or custom inches) and the side to design ---------- */
  const NOTES = {
    ship: ' Free shipping anywhere in the US, or free pickup at our Snohomish shop.',
    pickup: ' This size is pickup only at our Snohomish shop: we print it in-house.',
    back: ' You are designing the back. In the sign designer you choose 1 or 2 sides.',
    long: ' This shape is wider than the AI draws in one piece, so we design the middle and stretch the plain left and right ends to fill it.',
    tall: ' This shape is taller than the AI draws in one piece, so we design the middle and stretch the plain top and bottom to fill it.'
  };
  function chip(label, small, on, click, data) {
    const c = document.createElement('button');
    c.type = 'button';
    c.className = 'abi__chip';
    c.setAttribute('aria-pressed', String(!!on));
    c.textContent = label;
    if (small) { const sm = document.createElement('small'); sm.textContent = small; c.append(' ', sm); }
    Object.keys(data || {}).forEach((k) => { c.dataset[k] = data[k]; });
    c.addEventListener('click', click);
    return c;
  }
  function fillRow(row, sub, label, items, min) {
    row.innerHTML = '';
    items.forEach((it) => row.appendChild(chip(...it)));
    if (sub) sub.textContent = label || '';
    row.hidden = items.length < (min || 2);
    if (sub) sub.hidden = row.hidden;
  }
  // "We ship sizes from 6 x 6 in up to 48 x 38 in (either way). Bigger, up to 96 x 48 in, is pickup only at our Snohomish shop."
  function limitsTxt(m) {
    const mx = maxOf(m);
    const more = mx[0] > m.sm[0] || mx[1] > m.sm[1];
    return `${m.cn}: we ship sizes from ${m.mn[0]} x ${m.mn[1]} in up to ${m.sm[0]} x ${m.sm[1]} in (either way).${more ? ` Bigger, up to ${mx[0]} x ${mx[1]} in, is pickup only at our Snohomish shop.` : ''}`;
  }
  // material key, sign size in inches (w x h), side (front | back), size chip key ('24x18' style or 'custom'; null = the
  // chip with that size when there is one, else custom). A size outside the material's limits falls back to its default.
  function setSign(key, w, h, face, sizeKey) {
    const m = matOf(key) || matOf(DEFAULT_MAT);
    let sw = Number(w), sh = Number(h), sk = sizeKey;
    if (!sizeOk(m, sw, sh)) { sw = m.d[0]; sh = m.d[1]; sk = null; }
    const hit = m.sz.find((z) => z !== 'c' && Math.abs(z[0] - sw) < 1e-9 && Math.abs(z[1] - sh) < 1e-9);
    sk = sk === 'custom' || !hit ? 'custom' : keyOf(sw, sh);
    S.mat = m.k;
    S.iw = sw;
    S.ih = sh;
    S.sizeKey = sk;
    S.bad = '';
    S.face = faceOk(m, face) ? face : 'front';
    const r = reqOf(sw, sh);
    S.w = r.w;
    S.h = r.h;
    matChips.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.card === m.c)));
    const fins = MATS.filter((x) => x.c === m.c);
    fillRow(finRow, finSub, 'Finish', fins.length > 1 ? fins.map((x) => [x.fn, '', x.k === m.k, () => setSign(x.k, S.iw, S.ih, S.face, S.sizeKey), { mat: x.k }]) : []);
    sizeRow.innerHTML = '';
    m.sz.forEach((z) => {
      if (z === 'c') { sizeRow.appendChild(chip('Custom size', '', sk === 'custom', () => setCustom(), { size: 'custom' })); return; }
      sizeRow.appendChild(chip(`${fmtIn(z[0])} x ${fmtIn(z[1])}`, z[2] ? 'Pickup only' : '', sk === keyOf(z[0], z[1]), () => setSign(S.mat, z[0], z[1], S.face, keyOf(z[0], z[1])), { size: keyOf(z[0], z[1]) }));
    });
    customBox.hidden = sk !== 'custom';
    [wIn, hIn].forEach((el) => { setInvalid(el, false); el.min = String(m.mn[0]); el.max = String(maxOf(m)[0]); });
    if (document.activeElement !== wIn) wIn.value = fmtIn(sw);
    if (document.activeElement !== hIn) hIn.value = fmtIn(sh);
    limitsEl.textContent = limitsTxt(m);
    fillRow(faceRow, faceSub, 'Side to design', m.sd > 1 ? ['front', 'back'].map((id) => [faceName(id), sizeTxt(sw, sh), id === S.face, () => setSign(S.mat, S.iw, S.ih, id, S.sizeKey), { face: id }]) : []);
    drawShape();
    showErr('', sizeErr);
  }
  function drawShape() {
    const m = matOf(S.mat) || matOf(DEFAULT_MAT), fit = fitOf(m, S.iw, S.ih), ar = S.iw / S.ih;
    try { drawSign(shapeCv, null, m.k, S.iw, S.ih, false, 300, 300, true); shapeCv.hidden = false; } catch (e) { shapeCv.hidden = true; }
    shapeLabel.textContent = `${m.n} · ${sizeTxt(S.iw, S.ih)}${m.sd > 1 ? ` · ${faceName(S.face)}` : ''}${fit === 'pickup' ? ' · Pickup only' : ''}`;
    sizeNote.textContent = m.nt + (fit === 'pickup' ? NOTES.pickup : NOTES.ship) + (S.face === 'back' ? NOTES.back : '') + (ar > 3.05 ? NOTES.long : ar < 1 / 3.05 ? NOTES.tall : '');
  }
  function setCustom() {
    if (S.sizeKey !== 'custom') setSign(S.mat, S.iw, S.ih, S.face, 'custom');
    wIn.focus({ preventScroll: true });
  }
  // custom width / height typed in inches (decimals OK): a valid size is used right away; a wrong one keeps the last
  // good size and is explained when the field is left (or on Design my sign)
  function readCustom(final) {
    const m = matOf(S.mat) || matOf(DEFAULT_MAT);
    const w = round2(num(wIn.value)), h = round2(num(hIn.value));
    const f = fitOf(m, w, h);
    if (f === 'ship' || f === 'pickup') {
      if (w !== S.iw || h !== S.ih || S.bad) setSign(S.mat, w, h, S.face, 'custom');
      return;
    }
    const mx = maxOf(m);
    S.bad = f === 'small' ? `The smallest ${m.s} we make is ${m.mn[0]} x ${m.mn[1]} in.`
      : f === 'big' ? `That is bigger than we make. ${m.cn} goes up to ${m.sm[0]} x ${m.sm[1]} in shipped${mx !== m.sm ? ` and ${mx[0]} x ${mx[1]} in for pickup` : ''} (either way).`
      : 'Type the width and height in inches.';
    if (final) {
      setInvalid(wIn, !(w >= m.mn[0]) || f === 'big');
      setInvalid(hIn, !(h >= m.mn[1]) || f === 'big');
      showErr(S.bad, sizeErr);
    }
  }
  [wIn, hIn].forEach((el) => {
    el.addEventListener('input', () => readCustom(false));
    el.addEventListener('change', () => readCustom(true));
    el.addEventListener('blur', () => readCustom(true));
  });
  swapBtn.addEventListener('click', () => {
    const a = wIn.value;
    wIn.value = hIn.value;
    hIn.value = a;
    readCustom(true);
  });
  matChips.forEach((b) => {
    const list = MATS.filter((x) => x.c === b.dataset.card);
    b.hidden = !list.length;
    b.addEventListener('click', () => {
      const cur = matOf(S.mat);
      if (!list.length || (cur && cur.c === b.dataset.card)) return;
      // keep the size when the new material makes it (its chip, or custom), else its default size
      const nm = list[0], keep = sizeOk(nm, S.iw, S.ih);
      setSign(nm.k, keep ? S.iw : nm.d[0], keep ? S.ih : nm.d[1], S.face, keep && S.sizeKey === 'custom' ? 'custom' : null);
    });
  });

  /* ---------- sign ideas: a starting headline, style and style notes (all editable) ---------- */
  const IDEAS = {
    'grand-opening': { h: 'GRAND OPENING', style: 'fun', notes: 'Grand opening sign, bright and festive, easy to read from the street', sub: 'e.g. This Saturday · 10 AM' },
    'now-open': { h: 'NOW OPEN', style: 'bold', notes: 'Now open sign for a business, big clear words', sub: 'e.g. Come on in' },
    'now-hiring': { h: 'NOW HIRING', style: 'bold', notes: 'Now hiring sign, bold and easy to read from a passing car', sub: 'e.g. Apply inside' },
    sale: { h: 'SALE', style: 'bold', notes: 'Store sale sign, bold colors and big words', sub: 'e.g. 20% off this weekend' },
    hours: { h: 'OPEN', style: 'clean', notes: 'Business hours sign, clean and easy to read, the hours in a neat list', sub: 'e.g. Mon-Fri 9 AM - 5 PM' },
    parking: { h: 'RESERVED PARKING', style: 'clean', notes: 'Reserved parking sign, simple and clear, high contrast', sub: 'e.g. Customers only' },
    welcome: { h: 'WELCOME', style: 'elegant', notes: 'Welcome sign for a business entrance, warm and inviting', sub: 'e.g. Your Business' },
    vehicle: { h: '', ph: 'e.g. Your Business', style: 'bold', notes: 'Vehicle door sign with the company name, phone number and services, easy to read from a distance', sub: 'e.g. Plumbing · Heating · Repairs' },
    lobby: { h: '', ph: 'e.g. Your Business', style: 'elegant', notes: 'Lobby logo sign, refined and simple, lots of space around the name', sub: 'e.g. Suite 200' }
  };
  const HEAD_PH = lineIns.headline.placeholder;
  const ideaChips = $$('[data-ars-ideas] .abi__chip');
  let styleTouched = false;
  styleBtns.forEach((b) => b.addEventListener('click', () => { styleTouched = true; }));
  function setIdea(id) {
    const it = IDEAS[id];
    if (!it) return;
    const prev = IDEAS[S.idea];
    const h = lineIns.headline;
    if (!clean(h.value) || (prev && prev.h && clean(h.value) === prev.h)) {
      h.value = it.h;
      syncCount('headline', h);
      if (it.h) { setInvalid(h, false); showErr('', headErr); }
    }
    h.placeholder = it.ph || HEAD_PH;
    if (!clean(notesIn.value) || (prev && clean(notesIn.value) === prev.notes)) { notesIn.value = it.notes; syncCount('notes', notesIn); }
    lineIns.subline.placeholder = it.sub;
    if (!styleTouched) setStyle(it.style);
    S.idea = id;
    ideaChips.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.idea === id)));
    track('ardor_rigid_ai_idea', { idea: id });
  }
  ideaChips.forEach((b) => b.addEventListener('click', () => setIdea(b.dataset.idea)));
  /* ---------- logo: any image -> PNG (max 1536 px long side) in the browser ---------- */
  const LOGO_TYPES = /^image\/(png|jpeg|jpg|pjpeg|webp|svg\+xml|gif)$/i;
  const LOGO_EXT = /\.(png|jpe?g|webp|svg|gif)$/i;
  function loadImg(src) {
    return new Promise((res, rej) => {
      const im = new Image();
      im.decoding = 'async';
      im.onload = () => res(im);
      im.onerror = () => rej(new Error('image'));
      im.src = src;
    });
  }
  function canvasBlob(img, w, h, type = 'image/png') {
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    const ctx = c.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, w, h);
    return new Promise((res, rej) => c.toBlob((b) => (b ? res(b) : rej(new Error('toBlob'))), type));
  }
  function thumbData(img, w, h) {
    const s = Math.min(1, 160 / Math.max(w, h));
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(w * s));
    c.height = Math.max(1, Math.round(h * s));
    c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
    try { return c.toDataURL('image/png'); } catch (e) { return ''; }
  }
  async function svgSource(file) {
    const txt = await file.text();
    const doc = new DOMParser().parseFromString(txt, 'image/svg+xml');
    const svg = doc.documentElement;
    if (!svg || svg.nodeName.toLowerCase() !== 'svg' || doc.getElementsByTagName('parsererror').length) throw new Error('svg');
    const dim = (a) => { const v = svg.getAttribute(a) || ''; return /%/.test(v) ? NaN : parseFloat(v); };
    let vw = dim('width');
    let vh = dim('height');
    const vb = (svg.getAttribute('viewBox') || '').trim().split(/[\s,]+/).map(Number);
    const hasVb = vb.length === 4 && vb[2] > 0 && vb[3] > 0;
    if (hasVb && !(vw > 0 && vh > 0)) { vw = vb[2]; vh = vb[3]; }
    if (!(vw > 0 && vh > 0)) { vw = 1024; vh = 1024; }
    if (!hasVb) svg.setAttribute('viewBox', `0 0 ${vw} ${vh}`);
    const s = MAX_LOGO_PX / Math.max(vw, vh); // vector: always render at the full 1536 px long side
    const w = Math.max(1, Math.round(vw * s));
    const h = Math.max(1, Math.round(vh * s));
    svg.setAttribute('width', String(w));
    svg.setAttribute('height', String(h));
    const src = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(svg)], { type: 'image/svg+xml' }));
    return { src, w, h };
  }
  async function toPng(file) {
    const isSvg = /svg/i.test(file.type) || /\.svg$/i.test(file.name);
    let src;
    let w;
    let h;
    if (isSvg) ({ src, w, h } = await svgSource(file));
    else src = URL.createObjectURL(file);
    try {
      const img = await loadImg(src);
      if (!isSvg) {
        const nw = img.naturalWidth;
        const nh = img.naturalHeight;
        if (!nw || !nh) throw new Error('size');
        const s = Math.min(1, MAX_LOGO_PX / Math.max(nw, nh));
        w = Math.max(1, Math.round(nw * s));
        h = Math.max(1, Math.round(nh * s));
      }
      let blob = await canvasBlob(img, w, h);
      for (let i = 0; i < 4 && blob.size > MAX_LOGO_BYTES; i++) {
        w = Math.max(1, Math.round(w * 0.75));
        h = Math.max(1, Math.round(h * 0.75));
        blob = await canvasBlob(img, w, h);
      }
      if (blob.size > MAX_LOGO_BYTES) throw new Error('big');
      return { blob, w, h, thumbData: thumbData(img, w, h) };
    } finally {
      URL.revokeObjectURL(src);
    }
  }
  function setLogo(L) {
    if (S.logo && S.logo.thumb && S.logo.thumb.indexOf('blob:') === 0) URL.revokeObjectURL(S.logo.thumb);
    S.logo = L;
    if (L) {
      logoImg.src = L.thumb || L.thumbData || '';
      logoName.textContent = L.name || 'Your logo';
      logoInfo.textContent = 'Added to every design';
    }
    logoRow.hidden = !L;
    drop.hidden = !!L;
  }
  async function takeLogo(file) {
    showErr('', logoErr);
    if (!(LOGO_TYPES.test(file.type || '') || LOGO_EXT.test(file.name || ''))) { showErr('Please choose a PNG, JPG, SVG, WEBP or GIF file.', logoErr); return; }
    if (file.size > MAX_SOURCE_BYTES) { showErr('That file is too big. Please use one under 30 MB.', logoErr); return; }
    drop.classList.add('is-busy');
    try {
      const out = await toPng(file);
      setLogo({ blob: out.blob, w: out.w, h: out.h, name: file.name, id: null, thumb: URL.createObjectURL(out.blob), thumbData: out.thumbData });
      track('ardor_rigid_ai_logo', {});
    } catch (e) {
      showErr("We couldn't read that file. Try saving your logo as a PNG or JPG.", logoErr);
    }
    drop.classList.remove('is-busy');
  }
  fileIn.addEventListener('change', () => {
    const f = fileIn.files && fileIn.files[0];
    if (f) takeLogo(f);
    fileIn.value = '';
  });
  ['dragenter', 'dragover'].forEach((t) => drop.addEventListener(t, () => drop.classList.add('is-over')));
  ['dragleave', 'drop'].forEach((t) => drop.addEventListener(t, () => drop.classList.remove('is-over')));
  $('[data-abi-logo-remove]').addEventListener('click', () => {
    setLogo(null);
    showErr('', logoErr);
    fileIn.focus();
  });
  async function uploadLogo() {
    const L = S.logo;
    if (!L) return '';
    if (L.id) return L.id;
    if (!L.blob) throw { message: 'Please add your logo again.' };
    const fd = new FormData();
    fd.append('file', L.blob, 'logo.png');
    fd.append('visitor', visitor);
    const j = await api('/api/banner/logo', fd, 60000);
    if (!j.ok || !j.logoId) throw j;
    L.id = j.logoId;
    return j.logoId;
  }

  /* ---------- wording + style ---------- */
  function syncCount(key, el) {
    const c = $(`[data-abi-count="${key}"]`);
    if (!c) return;
    const n = el.value.length;
    c.textContent = `${n}/${LIMITS[key]}`;
    c.classList.toggle('is-near', n >= LIMITS[key] - 5);
  }
  Object.keys(lineIns).forEach((k) => {
    lineIns[k].addEventListener('input', () => {
      syncCount(k, lineIns[k]);
      if (k === 'headline' && clean(lineIns[k].value)) { setInvalid(lineIns[k], false); showErr('', headErr); }
      showErr('');
    });
  });
  notesIn.addEventListener('input', () => syncCount('notes', notesIn));

  function setStyle(st) {
    S.style = STYLES[st] ? st : 'bold';
    styleBtns.forEach((b) => {
      const on = b.dataset.style === S.style;
      b.setAttribute('aria-checked', String(on));
      b.tabIndex = on ? 0 : -1;
    });
  }
  styleBtns.forEach((b) => {
    b.addEventListener('click', () => setStyle(b.dataset.style));
    b.addEventListener('keydown', (e) => {
      if (!['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].includes(e.key)) return;
      e.preventDefault();
      const i = styleBtns.indexOf(b);
      const n = styleBtns[(i + (e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1) + styleBtns.length) % styleBtns.length];
      setStyle(n.dataset.style);
      n.focus();
    });
  });

  function readForm(show) {
    const sc = matOf(S.mat);
    let first = null;
    if (!sc || S.bad || !sizeOk(sc, S.iw, S.ih) || !faceOk(sc, S.face) || !validFt(S.w) || !validFt(S.h)) {
      if (show) showErr(S.bad || 'Pick your material and a size it comes in.', sizeErr);
      first = (S.bad ? wIn : matChips[0]) || null;
    }
    const headline = clean(lineIns.headline.value).slice(0, LIMITS.headline);
    setInvalid(lineIns.headline, !headline);
    if (!headline) {
      if (show) showErr('Type the main words for your sign.', headErr);
      first = first || lineIns.headline;
    }
    if (first) {
      if (show) { first.focus({ preventScroll: true }); first.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
      return null;
    }
    const lines = { headline };
    const sub = clean(lineIns.subline.value).slice(0, LIMITS.subline);
    const det = clean(lineIns.details.value).slice(0, LIMITS.details);
    if (sub) lines.subline = sub;
    if (det) lines.details = det;
    return { w: S.w, h: S.h, mat: S.mat, iw: S.iw, ih: S.ih, sizeKey: S.sizeKey, face: S.face, idea: S.idea || '', style: S.style, notes: clean(notesIn.value).slice(0, LIMITS.notes), lines, hasLogo: !!S.logo };
  }

  /* ---------- busy states ---------- */
  function setBusyUi(on) {
    goBtn.disabled = on || S.off;
    goLabel.textContent = on ? 'Designing…' : 'Design my sign';
    againBtn.disabled = on;
    editBtn.disabled = on;
    $$('button, input', grid).forEach((el) => { el.disabled = on; });
  }
  let genTimer = 0;
  function placeholder(ar) {
    const c = document.createElement('div');
    c.className = 'abi__card is-loading';
    c.setAttribute('aria-hidden', 'true');
    c.innerHTML = `<div class="abi__frame" style="--ar:${ar}"><span class="abi__skel" style="--art:${clampAr(ar)}"><i></i><i></i><i></i></span></div><div class="abi__card-actions"><span class="abi__skel-btn"></span><span class="abi__skel-btn"></span></div>`;
    return c;
  }
  function startGenLoading(v) {
    S.busy = true;
    setBusyUi(true);
    results.hidden = false;
    loading.hidden = false;
    againRow.hidden = true;
    fineEl.hidden = true;
    echo.textContent = v.lines.headline;
    metaEl.textContent = `${signLabel(v.mat, v.iw, v.ih, v.face)} · ${STYLES[v.style]}`;
    const ar = frameArOf(v);
    grid.dataset.shape = 'sign';
    grid.setAttribute('aria-busy', 'true');
    grid.innerHTML = '';
    for (let i = 0; i < 4; i++) grid.appendChild(placeholder(ar));
    const steps = GEN_STEPS.filter((m) => !m[3] || (m[3] === 'logo' && v.hasLogo));
    const t0 = Date.now();
    let last = -1;
    const tick = () => {
      const t = (Date.now() - t0) / 1000;
      bar.style.width = `${Math.min(95, 100 * (1 - Math.exp(-t / 36)))}%`;
      let k = 0;
      steps.forEach((m, i) => { if (t >= m[0]) k = i; });
      if (k === last) return;
      last = k;
      statusEl.textContent = steps[k][1];
      stepEls.forEach((li, i) => {
        li.classList.toggle('is-done', i < steps[k][2]);
        li.classList.toggle('is-on', i === steps[k][2]);
      });
    };
    clearInterval(genTimer);
    tick();
    genTimer = setInterval(tick, 250);
  }
  function stopGenLoading() {
    clearInterval(genTimer);
    S.busy = false;
    setBusyUi(false);
    loading.hidden = true;
    bar.style.width = '0';
    againRow.hidden = false;
    fineEl.hidden = false;
    grid.removeAttribute('aria-busy');
  }
  function cardBusy(card, title, sub, slow) {
    const o = $('.abi__busy', card);
    const fill = $('.abi__busy-bar i', card);
    $('.abi__busy-t', card).textContent = title;
    $('.abi__busy-s', card).textContent = sub;
    o.hidden = false;
    card.classList.add('is-busy');
    const t0 = Date.now();
    const tm = setInterval(() => { fill.style.width = `${Math.min(95, 100 * (1 - Math.exp(-(Date.now() - t0) / 1000 / slow)))}%`; }, 250);
    return () => { clearInterval(tm); o.hidden = true; card.classList.remove('is-busy'); fill.style.width = '0'; };
  }
  function cardErr(card, msg) { const el = card && $('.abi__cerr', card); if (el) showErr(msg, el); }
  const findCard = (id) => grid.querySelector(`.abi__card[data-id="${window.CSS && CSS.escape ? CSS.escape(id) : id}"]`);

  /* ---------- busy edges (same check as the banner builder, ardor-banner-layers.js) ---------- */
  const EDGE_BUSY = 2.5;
  // How streaky a design's stretched ends will look. Looks at the outer 2% of each edge that gets stretched,
  // sampled about 96 steps along the edge, and measures change in both directions: plain colors, smooth
  // gradients and stripes that already run straight off the edge score near 0; photos, busy textures and
  // text or shapes cut by the edge score high. Returns { a, b, score, busy } (a = left/top, b = right/bottom).
  function edgeBusy(img, wide) {
    var iw = img.naturalWidth || img.width, ih = img.naturalHeight || img.height;
    if (!iw || !ih) return null;
    var L = wide ? ih : iw, st = Math.max(1, L / 96);
    var n = Math.max(16, Math.round(L / st)), d = Math.max(4, Math.round((wide ? iw : ih) * 0.02 / st)), depth = d * st;
    var cw = wide ? d : n, ch = wide ? n : d, res = [];
    for (var s = 0; s < 2; s++) {
      var c = document.createElement('canvas'); c.width = cw; c.height = ch;
      var x = c.getContext('2d', { willReadFrequently: true });
      x.imageSmoothingEnabled = true; x.imageSmoothingQuality = 'high';
      if (wide) x.drawImage(img, s ? iw - depth : 0, 0, depth, ih, 0, 0, cw, ch);
      else x.drawImage(img, 0, s ? ih - depth : 0, iw, depth, 0, 0, cw, ch);
      var p = x.getImageData(0, 0, cw, ch).data;
      // per block along the edge (8 blocks): mean change along the edge and across it, max over R, G, B
      var B = 8, al = new Float64Array(B), ac = new Float64Array(B), na = new Float64Array(B), nc = new Float64Array(B);
      for (var yy = 0; yy < ch; yy++) for (var xx = 0; xx < cw; xx++) {
        var i = (yy * cw + xx) * 4, along = wide ? yy : xx, blk = Math.min(B - 1, Math.floor(along * B / n));
        if (xx + 1 < cw) {
          var dx = Math.max(Math.abs(p[i] - p[i + 4]), Math.abs(p[i + 1] - p[i + 5]), Math.abs(p[i + 2] - p[i + 6]));
          if (wide) { ac[blk] += dx; nc[blk]++; } else { al[blk] += dx; na[blk]++; }
        }
        if (yy + 1 < ch) {
          var j = i + cw * 4, dy = Math.max(Math.abs(p[i] - p[j]), Math.abs(p[i + 1] - p[j + 1]), Math.abs(p[i + 2] - p[j + 2]));
          if (wide) { al[blk] += dy; na[blk]++; } else { ac[blk] += dy; nc[blk]++; }
        }
      }
      var sum = 0, top = 0;
      for (var k = 0; k < B; k++) {
        var v = Math.sqrt((na[k] ? al[k] / na[k] : 0) * (nc[k] ? ac[k] / nc[k] : 0));
        sum += v; if (v > top) top = v;
      }
      res.push(Math.max(sum / B, top * 0.5));
    }
    var score = Math.max(res[0], res[1]);
    return { a: res[0], b: res[1], score: score, busy: score > EDGE_BUSY };
  }


  /* ---------- end previews (same drawing as the banner builder's extend(), ardor-banner-layers.js) ---------- */
  // Draws the design at the banner's real shape with its ends filled, stretched (as the builder does by default)
  // or smooth (&ends=smooth), so the customer can pick before the print version is made. size = preview short side.
  function mkC(w, h) { const c = document.createElement('canvas'); c.width = Math.max(1, Math.round(w)); c.height = Math.max(1, Math.round(h)); return c; }
  function composeEnds(img, B, smooth, size) {
    // sign faces: extend the sides when the face is wider than the design (any aspect), else the top and bottom
    const nw = img.naturalWidth || img.width, nh = img.naturalHeight || img.height, wide = B > nw / nh;
    const k = Math.min(1, size / (wide ? nh : nw));
    const src = mkC(nw * k, nh * k), sx0 = src.getContext('2d');
    sx0.imageSmoothingEnabled = true; sx0.imageSmoothingQuality = 'high'; sx0.drawImage(img, 0, 0, src.width, src.height);
    const iw0 = src.width, ih0 = src.height;
    let cw, ch;
    if (wide) { ch = ih0; cw = Math.round(ih0 * B); } else { cw = iw0; ch = Math.round(iw0 / B); }
    const iw = wide ? Math.round(iw0 * ch / ih0) : cw, ih = wide ? ch : Math.round(ih0 * cw / iw0);
    const ox = wide ? Math.round((cw - iw) / 2) : 0, oy = wide ? 0 : Math.round((ch - ih) / 2);
    const cv = mkC(cw, ch), x = cv.getContext('2d');
    x.imageSmoothingEnabled = true; x.imageSmoothingQuality = 'high';
    const A = wide ? ['l', 0, 0, ox, ch, 'r', ox + iw, 0, cw - ox - iw, ch] : ['t', 0, 0, cw, oy, 'b', 0, oy + ih, cw, ch - oy - ih];
    for (let q = 0; q < 10; q += 5) (smooth ? soften : side)(x, src, iw0, ih0, A[q], A[q + 1], A[q + 2], A[q + 3], A[q + 4]);
    x.drawImage(src, ox, oy, iw, ih);
    if (smooth) {
      const f = Math.max(2, Math.round((wide ? iw : ih) * 0.05));
      if (wide) { soften(x, src, iw0, ih0, 'l', ox, 0, f, ch, 1); soften(x, src, iw0, ih0, 'r', ox + iw - f, 0, f, ch, 1); }
      else { soften(x, src, iw0, ih0, 't', 0, oy, cw, f, 1); soften(x, src, iw0, ih0, 'b', 0, oy + ih - f, cw, f, 1); }
    }
    return cv;
  }
  // soft fill for one side: the outer 6% of the design averaged into 6 steps along the edge, then
  // stretched smoothly over the side. feather = 1 draws it over the design's own edge, fading out inward.
  function soften(x, img, iw0, ih0, where, dx, dy, dw, dh, feather) {
    if (dw < 1 || dh < 1) return;
    var horiz = where === 'l' || where === 'r', band = Math.max(1, Math.round((horiz ? iw0 : ih0) * 0.06));
    var mid = horiz ? mkC(1, 64) : mkC(64, 1), mx = mid.getContext('2d');
    mx.imageSmoothingEnabled = true; mx.imageSmoothingQuality = 'high';
    if (where === 'l') mx.drawImage(img, 0, 0, band, ih0, 0, 0, 1, 64);
    if (where === 'r') mx.drawImage(img, iw0 - band, 0, band, ih0, 0, 0, 1, 64);
    if (where === 't') mx.drawImage(img, 0, 0, iw0, band, 0, 0, 64, 1);
    if (where === 'b') mx.drawImage(img, 0, ih0 - band, iw0, band, 0, 0, 64, 1);
    var small = horiz ? mkC(1, 6) : mkC(6, 1), smx = small.getContext('2d');
    smx.imageSmoothingEnabled = true; smx.imageSmoothingQuality = 'high';
    smx.drawImage(mid, 0, 0, small.width, small.height);
    var soft = mkC(dw, dh), so = soft.getContext('2d');
    so.imageSmoothingEnabled = true; so.imageSmoothingQuality = 'high';
    so.drawImage(small, 0, 0, dw, dh);
    if (feather) {
      var g = where === 'l' ? so.createLinearGradient(0, 0, dw, 0) : where === 'r' ? so.createLinearGradient(dw, 0, 0, 0)
        : where === 't' ? so.createLinearGradient(0, 0, 0, dh) : so.createLinearGradient(0, dh, 0, 0);
      g.addColorStop(0, 'rgba(0,0,0,1)'); g.addColorStop(1, 'rgba(0,0,0,0)');
      so.globalCompositeOperation = 'destination-in'; so.fillStyle = g; so.fillRect(0, 0, dw, dh);
    }
    x.drawImage(soft, dx, dy);
  }
  function side(x, img, iw0, ih0, where, dx, dy, dw, dh) {
    if (dw < 1 || dh < 1) return;
    var horiz = where === 'l' || where === 'r', n = Math.max(1, Math.min(4, horiz ? iw0 : ih0));
    var line = horiz ? mkC(1, dh) : mkC(dw, 1), lx = line.getContext('2d');
    lx.imageSmoothingEnabled = true; lx.imageSmoothingQuality = 'high';
    if (where === 'l') lx.drawImage(img, 0, 0, n, ih0, 0, 0, 1, dh);
    if (where === 'r') lx.drawImage(img, iw0 - n, 0, n, ih0, 0, 0, 1, dh);
    if (where === 't') lx.drawImage(img, 0, 0, iw0, n, 0, 0, dw, 1);
    if (where === 'b') lx.drawImage(img, 0, ih0 - n, iw0, n, 0, 0, dw, 1);
    var sharp = mkC(dw, dh), sx = sharp.getContext('2d');
    sx.imageSmoothingEnabled = false;
    sx.drawImage(line, 0, 0, dw, dh);
    var len = horiz ? dh : dw, k = Math.max(2, Math.round(len / 40));
    var small = horiz ? mkC(1, Math.max(2, Math.round(dh / k))) : mkC(Math.max(2, Math.round(dw / k)), 1), smx = small.getContext('2d');
    smx.imageSmoothingEnabled = true; smx.imageSmoothingQuality = 'high';
    smx.drawImage(line, 0, 0, small.width, small.height);
    var soft = mkC(dw, dh), so = soft.getContext('2d');
    so.imageSmoothingEnabled = true; so.imageSmoothingQuality = 'high';
    so.drawImage(small, 0, 0, dw, dh);
    var fade = Math.max(1, Math.min(horiz ? dw : dh, len * 0.3)), g;
    if (where === 'l') g = so.createLinearGradient(dw, 0, dw - fade, 0);
    else if (where === 'r') g = so.createLinearGradient(0, 0, fade, 0);
    else if (where === 't') g = so.createLinearGradient(0, dh, 0, dh - fade);
    else g = so.createLinearGradient(0, 0, 0, fade);
    g.addColorStop(0, 'rgba(0,0,0,0)'); g.addColorStop(1, 'rgba(0,0,0,1)');
    so.globalCompositeOperation = 'destination-in'; so.fillStyle = g; so.fillRect(0, 0, dw, dh);
    sx.drawImage(soft, 0, 0);
    x.drawImage(sharp, dx, dy);
  }

  /* ---------- "How should we fill the ends?" (busy designs on banners wider than 3:1 or taller than 1:3) ---------- */
  function askEnds(card, img) {
    if (S.busy || !S.batch) return;
    let panel = $('.abi__ends', card);
    if (panel) { panel.hidden = false; card.classList.add('is-asking'); $('[data-ends="smooth"]', panel).focus(); return; }
    const b = S.batch, real = b.w / b.h, wide = real > img.w / img.h;
    const k = `abi-ends-${++uid}`;
    panel = document.createElement('div');
    panel.className = 'abi__ends';
    panel.setAttribute('role', 'group');
    panel.setAttribute('aria-labelledby', k);
    panel.innerHTML = `
      <p class="abi__ends-t" id="${k}"><b>How should we fill the ends?</b></p>
      <div class="abi__ends-opts">
        <button type="button" class="abi__endopt is-rec" data-ends="smooth"><span class="abi__endpic"></span><b>Smooth ends</b><small>A soft blend of the edge colors. Recommended</small></button>
        <button type="button" class="abi__endopt" data-ends="stretch"><span class="abi__endpic"></span><b>Stretched ends</b><small>The edges stretched out as they are</small></button>
      </div>
      <button type="button" class="abi__ends-back">Pick another design</button>`;
    $('.abi__ends-t', panel).append(` Your ${signShort(b)} is ${wide ? 'wider' : 'taller'} than this design, so we fill the ${wide ? 'left and right' : 'top and bottom'} ends from its edges. Its edges are busy, so stretched ends may look streaky.`);
    const im = $('.abi__img', card);
    [['smooth', true], ['stretch', false]].forEach(([key, smooth]) => {
      try { $(`[data-ends="${key}"] .abi__endpic`, panel).appendChild(signPic(im, b, smooth, 260)); } catch (e) { /* preview not drawable: the labels still explain it */ }
      $(`[data-ends="${key}"]`, panel).addEventListener('click', () => { closeEnds(card); track('ardor_rigid_ai_ends', { ends: key }); useDesign(img.id, key); });
    });
    $('.abi__ends-back', panel).addEventListener('click', () => { closeEnds(card); $('[data-act="use"]', card).focus({ preventScroll: true }); });
    $('.abi__card-actions', card).before(panel);
    card.classList.add('is-asking');
    $('[data-ends="smooth"]', panel).focus({ preventScroll: true });
    panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
  function closeEnds(card) { const p = $('.abi__ends', card); if (p) p.hidden = true; card.classList.remove('is-asking'); }

  /* ---------- results ---------- */
  let uid = 0;
  function cardFor(img, i, fixed) {
    const b = S.batch;
    const real = b.w / b.h;
    const art = img.w > 0 && img.h > 0 ? img.w / img.h : clampAr(real);
    const ext = Math.abs(real - art) / art > 0.03;
    const flagged = !!(img.check && img.check.ok === false);
    const n = i + 1;
    const k = `abi-fix-${++uid}`;
    const card = document.createElement('article');
    card.className = `abi__card${flagged ? ' is-flagged' : ''}`;
    card.setAttribute('role', 'listitem');
    card.setAttribute('aria-label', `Design ${n}`);
    card.dataset.id = img.id;
    card.innerHTML = `
      <div class="abi__frame ars__frame ars__frame--${layoutOf(b)}" style="--ar:${frameArOf(b)}">
        <div class="ars__mock"><canvas class="ars__cv" aria-hidden="true"></canvas></div>
        <div class="ars__flat"><img class="abi__img" alt="" decoding="async"><small class="ars__cap">${(matOf(b.mat) || { sd: 1 }).sd > 1 ? faceName(b.face) : 'Flat'} artwork</small></div>
        ${fixed ? '<span class="abi__badge">Fixed</span>' : ''}
      </div>
      ${ext ? '<p class="abi__extnote"></p>' : ''}
      ${flagged ? '<p class="abi__flag"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3.5 2.5 20h19L12 3.5zM12 10v4.5M12 17.5v.01"/></svg><span>This one may have a spelling or logo mistake — use Fix a word or pick another.</span></p>' : ''}
      <p class="abi__cerr" role="alert" hidden></p>
      <div class="abi__card-actions">
        <button type="button" class="ws-btn abi__use" data-act="use">Use this design<span class="abi__vh"> (design ${n})</span><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg></button>
        <button type="button" class="ws-btn ws-btn--ghost abi__fixbtn" data-act="fix" aria-expanded="false" aria-controls="${k}">Fix a word<span class="abi__vh"> (design ${n})</span></button>
      </div>
      <form class="abi__fix" id="${k}" hidden novalidate>
        <label class="abi__flabel" for="${k}-in">What should we fix?</label>
        <div class="abi__fix-row">
          <input id="${k}-in" class="abi__input" type="text" maxlength="${LIMITS.fix}" autocomplete="off">
          <button type="submit" class="ws-btn ws-btn--ghost">Fix it</button>
        </div>
        <small class="abi__fix-left"></small>
      </form>
      <div class="abi__busy" hidden><span class="abi__spin" aria-hidden="true"></span><b class="abi__busy-t"></b><small class="abi__busy-s"></small><span class="abi__busy-bar" aria-hidden="true"><i></i></span></div>`;
    const im = $('.abi__img', card);
    im.alt = `Sign design ${n}: ${b.lines.headline}`;
    im.addEventListener('load', () => {
      card.classList.add('is-in');
      try { cardPic($('.ars__cv', card), im, b); } catch (e) { card.classList.add('no-mock'); }
      if (!ext) return;
      let eb = null;
      try { eb = edgeBusy(im, real > art); } catch (e) { /* image not readable: keep the plain note */ }
      if (!eb || !eb.busy) return;
      card.dataset.busy = '1';
      const note = $('.abi__extnote', card);
      note.classList.add('is-warn');
      note.textContent = `Busy edges: the stretched ${real > art ? 'left and right' : 'top and bottom'} ends of this design may look streaky. When you use it, you can pick smooth ends instead.`;
    });
    im.addEventListener('error', () => card.classList.add('is-broken'));
    im.crossOrigin = 'anonymous'; // the sign picture and the busy-edge check read the pixels (the worker sends CORS *)
    im.src = img.url;
    if (ext) $('.abi__extnote', card).textContent = `We stretch the plain ${real > art ? 'left and right' : 'top and bottom'} ends to fill your ${signShort(b)}.`;
    const fixForm = $('.abi__fix', card);
    const fixIn = $('input', fixForm);
    const fixBtn = $('[data-act="fix"]', card);
    fixIn.placeholder = 'e.g. Change "Snohomsh" to "Snohomish"';
    $('.abi__fix-left', card).textContent = fixesText();
    $('[data-act="use"]', card).addEventListener('click', () => (card.dataset.busy ? askEnds(card, img) : useDesign(img.id)));
    fixBtn.addEventListener('click', () => {
      const open = fixForm.hidden;
      fixForm.hidden = !open;
      fixBtn.setAttribute('aria-expanded', String(open));
      if (!open) return;
      const wrong = img.check && Array.isArray(img.check.wrong) ? img.check.wrong.find((x) => x && x.expected && x.seen) : null;
      if (wrong && !fixIn.value) fixIn.value = `Change "${wrong.seen}" to "${wrong.expected}"`;
      fixIn.focus();
    });
    fixForm.addEventListener('submit', (e) => { e.preventDefault(); fixDesign(img.id, fixIn.value); });
    if (S.busy) $$('button, input', card).forEach((el) => { el.disabled = true; });
    return card;
  }
  function renderResults() {
    const b = S.batch;
    if (!b || !b.images || !b.images.length) { results.hidden = true; return; }
    results.hidden = false;
    loading.hidden = true;
    againRow.hidden = false;
    fineEl.hidden = false;
    echo.textContent = b.lines.headline;
    metaEl.textContent = `${signLabel(b.mat, b.iw, b.ih, b.face)} · ${STYLES[b.style] || ''}`;
    grid.dataset.shape = 'sign';
    grid.innerHTML = '';
    b.images.forEach((img, i) => grid.appendChild(cardFor(img, i, !!img.fixedFrom)));
    const bad = b.images.filter((x) => x.check && x.check.ok === false).length;
    if (bad && bad === b.images.length) statusEl.textContent = 'These may have a mistake. Use Fix a word or make another set.';
    else statusEl.textContent = b.images.length > 1 ? `${b.images.length} designs ready. Pick your favorite.` : 'Your design is ready.';
  }
  function remember() {
    const L = S.logo;
    sset(LAST_KEY, {
      at: Date.now(),
      batch: S.batch,
      form: { mat: S.mat, iw: S.iw, ih: S.ih, sizeKey: S.sizeKey, face: S.face, idea: S.idea || '', style: S.style, notes: notesIn.value, headline: lineIns.headline.value, subline: lineIns.subline.value, details: lineIns.details.value },
      logo: L && L.id ? { id: L.id, name: L.name, w: L.w, h: L.h, thumbData: L.thumbData } : null
    });
  }

  /* ---------- generate / fix / final ---------- */
  async function generate() {
    if (S.busy || S.off) return;
    const v = readForm(true);
    if (!v) return;
    if (!S.hasEmail && S.bannersLeft === 0) { S.pending = { kind: 'generate', run: generate }; openGate(); return; }
    showErr('');
    const prev = S.batch;
    startGenLoading(v);
    results.scrollIntoView({ behavior: 'smooth', block: 'start' });
    let logoId = '';
    if (v.hasLogo) {
      try {
        logoId = await uploadLogo();
      } catch (j) {
        stopGenLoading();
        S.batch = prev;
        renderResults();
        showErr((j && j.message) || "We couldn't upload your logo. Try another file, or remove it and try again.");
        form.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
    }
    const token = await botToken();
    const body = withEmail({ visitor, token, product: 'banner', widthFt: v.w, heightFt: v.h, style: v.style, styleNotes: v.notes, lines: v.lines });
    if (logoId) body.logoId = logoId;
    const j = await api('/api/banner/generate', body);
    stopGenLoading();
    if (j.bannersLeft != null) S.bannersLeft = j.bannersLeft;
    if (j.fixesLeft != null) S.fixesLeft = j.fixesLeft;
    if (!j.ok || !Array.isArray(j.images) || !j.images.length) {
      S.batch = prev;
      renderResults();
      if (j.error === 'need_email') {
        S.bannersLeft = 0;
        setQuota();
        S.pending = { kind: 'generate', run: generate };
        openGate();
        return;
      }
      showErr(j.message || "We couldn't make your designs this time. Please try again.");
      setQuota();
      form.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if ('hasEmail' in j) S.hasEmail = !!j.hasEmail;
    S.batch = {
      batchId: j.batchId || '', aspect: j.aspect, clamped: !!j.clamped,
      w: v.w, h: v.h, mat: v.mat, iw: v.iw, ih: v.ih, sizeKey: v.sizeKey, face: v.face, idea: v.idea, style: v.style, notes: v.notes, lines: v.lines,
      logoId: logoId || null, images: j.images.slice(0, 8)
    };
    setQuota();
    renderResults();
    remember();
    h2.focus({ preventScroll: true });
    track('ardor_rigid_ai_generated', { style: v.style, mat: v.mat, w: v.iw, h: v.ih, face: v.face, idea: v.idea || '', logo: !!logoId, shown: j.images.length });
  }

  async function fixDesign(id, text) {
    if (S.busy) return;
    const card = findCard(id);
    if (!card) return;
    const input = $('.abi__fix input', card);
    const fix = clean(text).slice(0, LIMITS.fix);
    if (fix.length < 2) { cardErr(card, 'Tell us what to fix, like: Change "Snohomsh" to "Snohomish".'); input.focus(); return; }
    if (!S.hasEmail && S.fixesLeft === 0) { S.pending = { kind: 'fix', run: () => fixDesign(id, fix) }; openGate(); return; }
    cardErr(card, '');
    S.busy = true;
    setBusyUi(true);
    const done = cardBusy(card, 'Fixing it…', 'Redoing just that part', 14);
    statusEl.textContent = 'Fixing your design…';
    const token = await botToken();
    const j = await api('/api/banner/fix', withEmail({ visitor, token, imageId: id, fix }));
    done();
    S.busy = false;
    setBusyUi(false);
    if (j.fixesLeft != null) S.fixesLeft = j.fixesLeft;
    if (!j.ok || !Array.isArray(j.images) || !j.images.length) {
      setQuota();
      statusEl.textContent = '';
      if (j.error === 'need_email') { S.fixesLeft = 0; S.pending = { kind: 'fix', run: () => fixDesign(id, fix) }; openGate(); return; }
      cardErr(card, j.message || 'That fix did not work. Please try again.');
      return;
    }
    const idx = S.batch.images.findIndex((x) => x.id === id);
    const next = Object.assign({}, j.images[0], { fixedFrom: id });
    S.batch.images[idx] = next;
    const fresh = cardFor(next, idx, true);
    card.replaceWith(fresh);
    setQuota();
    remember();
    statusEl.textContent = next.check && next.check.ok === false
      ? 'Here is your fixed design. It may still have a mistake, so check it closely.'
      : 'Here is your fixed design.';
    $('[data-act="use"]', fresh).focus({ preventScroll: true });
    track('ardor_rigid_ai_fixed', { style: S.batch.style });
  }

  function validBox(lb) {
    return Array.isArray(lb) && lb.length === 4 && lb.every((n) => typeof n === 'number' && Number.isFinite(n)) ? lb.map((n) => Math.min(1, Math.max(0, n))) : null;
  }
  function handoffUrl(aiId, b, logoId, lb, ends) {
    const m = matOf(b.mat) || matOf(DEFAULT_MAT);
    let u = `${BUILDER}${BUILDER.indexOf('?') === -1 ? '?' : '&'}ai=${enc(aiId)}&mat=${enc(m.k)}&w=${enc(fmtIn(b.iw))}&h=${enc(fmtIn(b.ih))}&face=${enc(faceOk(m, b.face) ? b.face : 'front')}`;
    if (ends === 'smooth') u += '&ends=smooth';
    if (logoId) {
      u += `&logo=${enc(logoId)}`;
      if (lb) u += `&lb=${lb.map((n) => n.toFixed(4)).join(',')}`;
    }
    return u;
  }
  async function useDesign(id, ends) {
    if (S.busy || !S.batch) return;
    const card = findCard(id);
    const b = S.batch;
    const img = b.images.find((x) => x.id === id);
    if (!card || !img) return;
    cardErr(card, '');
    S.busy = true;
    setBusyUi(true);
    const done = cardBusy(card, 'Making a sharp, print-ready version…', 'about 30–60 seconds', 25);
    statusEl.textContent = 'Making a sharp, print-ready version… about 30–60 seconds.';
    const token = await botToken();
    const j = await api('/api/banner/final', withEmail({ visitor, token, imageId: id }));
    if (j.finalsLeft != null) S.finalsLeft = j.finalsLeft;
    if (!j.ok || !j.image || !j.image.id) {
      done();
      S.busy = false;
      setBusyUi(false);
      statusEl.textContent = '';
      if (j.error === 'need_email') { S.pending = { kind: 'final', run: () => useDesign(id, ends) }; openGate(); return; }
      cardErr(card, j.message || "We couldn't make the print version. Please try again.");
      return;
    }
    const fin = j.image;
    const lb = validBox(fin.check && fin.check.logoBox) || validBox(img.check && img.check.logoBox);
    const url = handoffUrl(fin.id, b, b.logoId, lb, ends);
    const m = matOf(b.mat);
    const pick = {
      v: 1, ai: fin.id, url: fin.url, w: fin.w || 0, h: fin.h || 0,
      mat: m ? m.k : b.mat, matName: m ? m.n : '', card: m ? m.c : '',
      sizeIn: { w: b.iw, h: b.ih }, sizeKey: b.sizeKey || 'custom', pickupOnly: !!m && fitOf(m, b.iw, b.ih) === 'pickup',
      sides: m ? m.sd : 1, face: m && faceOk(m, b.face) ? b.face : 'front', faceName: faceName(b.face),
      ends: ends === 'smooth' ? 'smooth' : 'stretch',
      reqFt: { w: b.w, h: b.h }, logo: b.logoId || null, lb: lb || null,
      preview: id, lines: b.lines, style: b.style, idea: b.idea || '', at: Date.now()
    };
    sset(PICK_KEY, pick);
    remember();
    track('ardor_rigid_ai_design_used', { style: b.style, mat: pick.mat, w: b.iw, h: b.ih, face: pick.face, logo: !!b.logoId, ends: ends || '' });
    $('.abi__busy-t', card).textContent = 'Opening the sign designer…';
    $('.abi__busy-s', card).textContent = '';
    statusEl.textContent = 'Opening the sign designer…';
    const ev = new CustomEvent('ardor:rigid-ai-handoff', { bubbles: true, cancelable: true, detail: { url, pick, image: fin, preview: img, batch: b } });
    if (root.dispatchEvent(ev)) { window.location.assign(url); return; }
    done();
    S.busy = false;
    setBusyUi(false);
  }

  form.addEventListener('submit', (e) => { e.preventDefault(); generate(); });
  againBtn.addEventListener('click', () => generate());
  editBtn.addEventListener('click', () => {
    form.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setTimeout(() => lineIns.headline.focus({ preventScroll: true }), 350);
  });

  /* ---------- email unlock (same as the sticker generator: Shopify customer form into a hidden iframe) ---------- */
  function openGate() {
    showErr('', gateErr);
    gateEmail.value = email || '';
    if (gateN) gateN.textContent = String(S.emailBanners);
    if (gate.showModal) { try { gate.showModal(); } catch (e) { gate.setAttribute('open', ''); } }
    else gate.setAttribute('open', '');
    setTimeout(() => gateEmail.focus(), 60);
    track('ardor_rigid_ai_gate_shown', {});
  }
  function closeGate() { if (gate.close) gate.close(); else gate.removeAttribute('open'); }
  $('[data-abi-gate-close]').addEventListener('click', closeGate);
  gate.addEventListener('click', (e) => { if (e.target === gate) closeGate(); });
  function customerForm() {
    let f = root.querySelector('form#abi-customer');
    if (!f) {
      f = document.createElement('form');
      f.id = 'abi-customer';
      f.method = 'post';
      f.action = '/contact#abi-customer';
      f.className = 'abi__customer';
      f.innerHTML = '<input type="hidden" name="form_type" value="customer"><input type="hidden" name="utf8" value="✓"><input type="hidden" name="contact[tags]" value="newsletter,ai-sign-generator,ardor-rigid-builder"><input type="hidden" name="contact[email]" data-abi-customer-email>';
      root.appendChild(f);
    }
    return f;
  }
  function subscribe(addr) {
    const f = customerForm();
    const field = f.querySelector('[name="contact[email]"]');
    if (field) field.value = addr;
    f.target = 'abi-sink';
    try { if (f.requestSubmit) f.requestSubmit(); else f.submit(); } catch (e) { try { f.submit(); } catch (e2) { /* ignore */ } }
  }
  gateForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const addr = gateEmail.value.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(addr)) { showErr('Please enter a valid email.', gateErr); gateEmail.focus(); return; }
    email = addr;
    store.set('wsIdeaEmail', addr);
    S.hasEmail = true;
    subscribe(addr);
    track('ardor_rigid_ai_email', {});
    const q = await api(`/api/banner/quota?visitor=${enc(visitor)}&email=${enc(addr)}`);
    if (q.ok) {
      S.bannersLeft = q.bannersLeft;
      S.fixesLeft = q.fixesLeft;
      S.finalsLeft = q.finalsLeft;
      if (q.emailBanners) S.emailBanners = q.emailBanners;
    }
    setQuota();
    closeGate();
    const next = S.pending;
    S.pending = null;
    if (next && !(next.kind === 'generate' && S.bannersLeft === 0)) next.run();
  });

  /* ---------- start ---------- */
  Object.keys(lineIns).forEach((k) => syncCount(k, lineIns[k]));
  syncCount('notes', notesIn);
  const params = new URL(window.location.href).searchParams;
  const last = sget(LAST_KEY);
  if (last && last.form && Date.now() - (last.at || 0) < 36e5 * 24) {
    const f = last.form;
    lineIns.headline.value = f.headline || '';
    lineIns.subline.value = f.subline || '';
    lineIns.details.value = f.details || '';
    notesIn.value = f.notes || '';
    Object.keys(lineIns).forEach((k) => syncCount(k, lineIns[k]));
    syncCount('notes', notesIn);
    setStyle(f.style || 'bold');
    if (f.idea && IDEAS[f.idea]) { S.idea = f.idea; ideaChips.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.idea === f.idea))); lineIns.subline.placeholder = IDEAS[f.idea].sub; }
    if (f.mat && matOf(f.mat)) setSign(f.mat, f.iw, f.ih, f.face, f.sizeKey);
    if (last.logo && last.logo.id) setLogo({ blob: null, id: last.logo.id, name: last.logo.name, w: last.logo.w, h: last.logo.h, thumb: last.logo.thumbData, thumbData: last.logo.thumbData });
    if (last.batch && last.batch.images && last.batch.images.length) { S.batch = last.batch; renderResults(); }
  }
  // ?mat=COR&w=24&h=18&face=back (or the same after #): that material, size and side; a size it does not come in keeps
  // its default size
  const pm = matOf(params.get('mat'));
  const pw = num(params.get('w')), ph = num(params.get('h'));
  const pf = String(params.get('face') || '').toLowerCase();
  const m0 = pm || matOf(S.mat) || matOf(DEFAULT_MAT);
  const useP = sizeOk(m0, round2(pw), round2(ph));
  if (pm || useP || FACE_NAMES[pf]) setSign(m0.k, useP ? round2(pw) : pm ? m0.d[0] : S.iw, useP ? round2(ph) : pm ? m0.d[1] : S.ih, FACE_NAMES[pf] ? pf : S.face, useP || pm ? null : S.sizeKey);
  else setSign(S.mat, S.iw, S.ih, S.face, S.sizeKey);

  (async () => {
    const q = await api(`/api/banner/quota?visitor=${enc(visitor)}${email ? `&email=${enc(email)}` : ''}`, null, 20000);
    if (!q.ok) return;
    if (q.enabled === false) {
      S.off = true;
      goBtn.disabled = true;
      showErr('The sign generator is taking a short break. Please try again later.');
    }
    S.bannersLeft = q.bannersLeft;
    S.fixesLeft = q.fixesLeft;
    S.finalsLeft = q.finalsLeft;
    S.hasEmail = !!q.hasEmail;
    if (q.freeBanners) S.freeBanners = q.freeBanners;
    if (q.emailBanners) S.emailBanners = q.emailBanners;
    setQuota();
  })();
  if (SITEKEY) loadTurnstile();
})();
